import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Chip,
  Grid,
  Link,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useForm, Controller } from 'react-hook-form';
import { trainingApi, TrainingRecord, CreateTrainingData, UpdateTrainingData, TrainingFilters } from '../services/training';
import { objectivesApi, Objective, CreateObjectiveData, UpdateObjectiveData, ObjectiveFilters, ObjectiveMilestone, CreateMilestoneData } from '../services/objectives';
import { payrollApi, Employee } from '../services/payroll';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import { CURRENCIES } from '../utils/currency';
import { useCompany } from '../contexts/CompanyContext';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TrainingAndObjectives: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [activeTab, setActiveTab] = useState(0); // 0 = Training, 1 = Objectives
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Training state
  const [trainings, setTrainings] = useState<TrainingRecord[]>([]);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [trainingDeleteDialogOpen, setTrainingDeleteDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState<TrainingRecord | null>(null);
  const [viewingTraining, setViewingTraining] = useState<TrainingRecord | null>(null);
  const [trainingToDelete, setTrainingToDelete] = useState<TrainingRecord | null>(null);
  const [trainingStatusFilter, setTrainingStatusFilter] = useState('all');
  
  // Objectives state
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [objectiveDialogOpen, setObjectiveDialogOpen] = useState(false);
  const [objectiveDeleteDialogOpen, setObjectiveDeleteDialogOpen] = useState(false);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [viewingObjective, setViewingObjective] = useState<Objective | null>(null);
  const [objectiveToDelete, setObjectiveToDelete] = useState<Objective | null>(null);
  const [objectiveStatusFilter, setObjectiveStatusFilter] = useState('all');
  const [objectivePriorityFilter, setObjectivePriorityFilter] = useState('all');
  
  // Employees for linking
  const [employees, setEmployees] = useState<Employee[]>([]);
  
  // Milestones state
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<ObjectiveMilestone | null>(null);
  const [currentObjectiveId, setCurrentObjectiveId] = useState<string | null>(null);
  
  const milestoneForm = useForm<CreateMilestoneData>({
    defaultValues: {
      title: '',
      description: '',
      targetDate: '',
      status: 'pending',
      orderIndex: 0,
    },
  });

  const trainingForm = useForm<CreateTrainingData>({
    defaultValues: {
      title: '',
      description: '',
      provider: '',
      trainingDate: format(new Date(), 'yyyy-MM-dd'),
      completionDate: '',
      cost: undefined,
      currency: baseCurrency,
      status: 'planned',
      certificateUrl: '',
      notes: '',
      employeeId: undefined,
      reminderDaysBefore: 7,
      createExpense: false,
    },
  });

  const objectiveForm = useForm<CreateObjectiveData>({
    defaultValues: {
      title: '',
      description: '',
      category: '',
      targetDate: '',
      completionDate: '',
      status: 'active',
      priority: 'medium',
      progress: 0,
      notes: '',
      employeeId: undefined,
    },
  });

  const loadEmployees = useCallback(async () => {
    try {
      const employeesData = await payrollApi.getEmployees();
      setEmployees(employeesData);
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  }, []);

  const loadTrainings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: TrainingFilters = {};
      if (trainingStatusFilter !== 'all') {
        filters.status = trainingStatusFilter as 'planned' | 'in_progress' | 'completed' | 'cancelled';
      }
      const data = await trainingApi.getAll(filters);
      setTrainings(data || []);
    } catch (err: any) {
      console.error('Error loading trainings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load training records');
    } finally {
      setLoading(false);
    }
  }, [trainingStatusFilter]);

  const loadObjectives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: ObjectiveFilters = {};
      if (objectiveStatusFilter !== 'all') {
        filters.status = objectiveStatusFilter as 'active' | 'completed' | 'cancelled' | 'on_hold';
      }
      if (objectivePriorityFilter !== 'all') {
        filters.priority = objectivePriorityFilter as 'low' | 'medium' | 'high';
      }
      const data = await objectivesApi.getAll(filters);
      setObjectives(data || []);
    } catch (err: any) {
      console.error('Error loading objectives:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load objectives');
    } finally {
      setLoading(false);
    }
  }, [objectiveStatusFilter, objectivePriorityFilter]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    if (activeTab === 0) {
      loadTrainings();
    } else {
      loadObjectives();
    }
  }, [activeTab, loadTrainings, loadObjectives]);

  // Training handlers
  const handleOpenCreateTraining = () => {
    trainingForm.reset({
      title: '',
      description: '',
      provider: '',
      trainingDate: format(new Date(), 'yyyy-MM-dd'),
      completionDate: '',
      cost: undefined,
      currency: baseCurrency,
      status: 'planned',
      certificateUrl: '',
      notes: '',
      employeeId: undefined,
      reminderDaysBefore: 7,
      createExpense: false,
    });
    setEditingTraining(null);
    setTrainingDialogOpen(true);
  };

  const handleCloseTrainingDialog = () => {
    setTrainingDialogOpen(false);
    setEditingTraining(null);
    trainingForm.reset();
  };

  const handleOpenEditTraining = (training: TrainingRecord) => {
    setEditingTraining(training);
    trainingForm.reset({
      title: training.title,
      description: training.description || '',
      provider: training.provider || '',
      trainingDate: format(parseISO(training.trainingDate), 'yyyy-MM-dd'),
      completionDate: training.completionDate ? format(parseISO(training.completionDate), 'yyyy-MM-dd') : '',
      cost: training.cost,
      currency: training.currency,
      status: training.status,
      certificateUrl: training.certificateUrl || '',
      notes: training.notes || '',
      employeeId: training.employeeId || undefined,
      reminderDaysBefore: training.reminderDaysBefore || 7,
      createExpense: false, // Don't auto-create expense on edit
    });
    setTrainingDialogOpen(true);
  };

  const handleOpenViewTraining = (training: TrainingRecord) => {
    setViewingTraining(training);
  };

  const handleCloseViewTraining = () => {
    setViewingTraining(null);
  };

  const handleOpenDeleteTraining = (training: TrainingRecord) => {
    setTrainingToDelete(training);
    setTrainingDeleteDialogOpen(true);
  };

  const handleCloseDeleteTraining = () => {
    setTrainingDeleteDialogOpen(false);
    setTrainingToDelete(null);
  };

  const handleCreateTraining = async (data: CreateTrainingData) => {
    try {
      setError(null);
      await trainingApi.create(data);
      handleCloseTrainingDialog();
      loadTrainings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create training record');
    }
  };

  const handleUpdateTraining = async (data: UpdateTrainingData) => {
    if (!editingTraining) return;
    try {
      setError(null);
      await trainingApi.update(editingTraining.id, data);
      handleCloseTrainingDialog();
      loadTrainings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update training record');
    }
  };

  const handleDeleteTraining = async () => {
    if (!trainingToDelete) return;
    try {
      setError(null);
      await trainingApi.delete(trainingToDelete.id);
      handleCloseDeleteTraining();
      loadTrainings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete training record');
    }
  };

  // Objectives handlers
  const handleOpenCreateObjective = () => {
    objectiveForm.reset({
      title: '',
      description: '',
      category: '',
      targetDate: '',
      completionDate: '',
      status: 'active',
      priority: 'medium',
      progress: 0,
      notes: '',
      employeeId: undefined,
    });
    setEditingObjective(null);
    setObjectiveDialogOpen(true);
  };

  const handleCloseObjectiveDialog = () => {
    setObjectiveDialogOpen(false);
    setEditingObjective(null);
    objectiveForm.reset();
  };

  const handleOpenEditObjective = (objective: Objective) => {
    setEditingObjective(objective);
    objectiveForm.reset({
      title: objective.title,
      description: objective.description || '',
      category: objective.category || '',
      targetDate: objective.targetDate ? format(parseISO(objective.targetDate), 'yyyy-MM-dd') : '',
      completionDate: objective.completionDate ? format(parseISO(objective.completionDate), 'yyyy-MM-dd') : '',
      status: objective.status,
      priority: objective.priority,
      progress: objective.progress,
      notes: objective.notes || '',
      employeeId: objective.employeeId || undefined,
    });
    setObjectiveDialogOpen(true);
  };

  const handleOpenViewObjective = (objective: Objective) => {
    setViewingObjective(objective);
  };

  const handleCloseViewObjective = () => {
    setViewingObjective(null);
  };

  const handleOpenDeleteObjective = (objective: Objective) => {
    setObjectiveToDelete(objective);
    setObjectiveDeleteDialogOpen(true);
  };

  const handleCloseDeleteObjective = () => {
    setObjectiveDeleteDialogOpen(false);
    setObjectiveToDelete(null);
  };

  const handleCreateObjective = async (data: CreateObjectiveData) => {
    try {
      setError(null);
      await objectivesApi.create(data);
      handleCloseObjectiveDialog();
      loadObjectives();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create objective');
    }
  };

  const handleUpdateObjective = async (data: UpdateObjectiveData) => {
    if (!editingObjective) return;
    try {
      setError(null);
      await objectivesApi.update(editingObjective.id, data);
      handleCloseObjectiveDialog();
      loadObjectives();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update objective');
    }
  };

  const handleDeleteObjective = async () => {
    if (!objectiveToDelete) return;
    try {
      setError(null);
      await objectivesApi.delete(objectiveToDelete.id);
      handleCloseDeleteObjective();
      loadObjectives();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete objective');
    }
  };

  // Milestone handlers
  const handleOpenCreateMilestone = (objectiveId: string) => {
    setCurrentObjectiveId(objectiveId);
    setEditingMilestone(null);
    milestoneForm.reset({
      title: '',
      description: '',
      targetDate: '',
      status: 'pending',
      orderIndex: 0,
    });
    setMilestoneDialogOpen(true);
  };

  const handleOpenEditMilestone = (milestone: ObjectiveMilestone, objectiveId: string) => {
    setCurrentObjectiveId(objectiveId);
    setEditingMilestone(milestone);
    milestoneForm.reset({
      title: milestone.title,
      description: milestone.description || '',
      targetDate: milestone.targetDate ? format(parseISO(milestone.targetDate), 'yyyy-MM-dd') : '',
      status: milestone.status,
      orderIndex: milestone.orderIndex,
    });
    setMilestoneDialogOpen(true);
  };

  const handleCloseMilestoneDialog = () => {
    setMilestoneDialogOpen(false);
    setEditingMilestone(null);
    setCurrentObjectiveId(null);
    milestoneForm.reset();
  };

  const handleCreateMilestone = async (data: CreateMilestoneData) => {
    if (!currentObjectiveId) return;
    try {
      setError(null);
      await objectivesApi.createMilestone(currentObjectiveId, data);
      handleCloseMilestoneDialog();
      loadObjectives();
      // Reload the viewing objective if it's the same
      if (viewingObjective && viewingObjective.id === currentObjectiveId) {
        const updated = await objectivesApi.getById(currentObjectiveId);
        setViewingObjective(updated);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create milestone');
    }
  };

  const handleUpdateMilestone = async (data: CreateMilestoneData) => {
    if (!editingMilestone || !currentObjectiveId) return;
    try {
      setError(null);
      await objectivesApi.updateMilestone(currentObjectiveId, editingMilestone.id, data);
      handleCloseMilestoneDialog();
      loadObjectives();
      // Reload the viewing objective if it's the same
      if (viewingObjective && viewingObjective.id === currentObjectiveId) {
        const updated = await objectivesApi.getById(currentObjectiveId);
        setViewingObjective(updated);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update milestone');
    }
  };

  const handleDeleteMilestone = async (objectiveId: string, milestoneId: string) => {
    try {
      setError(null);
      await objectivesApi.deleteMilestone(objectiveId, milestoneId);
      loadObjectives();
      // Reload the viewing objective if it's the same
      if (viewingObjective && viewingObjective.id === objectiveId) {
        const updated = await objectivesApi.getById(objectiveId);
        setViewingObjective(updated);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete milestone');
    }
  };

  const getTrainingStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getObjectiveStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'active': return 'primary';
      case 'on_hold': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  if (loading && (trainings.length === 0 && objectives.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Training & Objectives</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={activeTab === 0 ? handleOpenCreateTraining : handleOpenCreateObjective}
        >
          Add {activeTab === 0 ? 'Training' : 'Objective'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Training" />
          <Tab label="Objectives" />
        </Tabs>
      </Paper>

      {/* Training Tab */}
      {activeTab === 0 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={trainingStatusFilter}
                    label="Status"
                    onChange={(e) => setTrainingStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Training Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Training Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">No training records found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    trainings.map((training) => (
                      <TableRow key={training.id}>
                        <TableCell>{training.title}</TableCell>
                        <TableCell>{training.provider || '-'}</TableCell>
                        <TableCell>
                          {training.employeeId ? (employees.find(e => e.id === training.employeeId)?.name || 'Unknown') : '-'}
                        </TableCell>
                        <TableCell>
                          {format(parseISO(training.trainingDate), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={training.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            size="small"
                            color={getTrainingStatusColor(training.status) as any}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {training.cost ? formatCurrency(training.cost, training.currency) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenViewTraining(training)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditTraining(training)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteTraining(training)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Objectives Tab */}
      {activeTab === 1 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={objectiveStatusFilter}
                    label="Status"
                    onChange={(e) => setObjectiveStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="on_hold">On Hold</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={objectivePriorityFilter}
                    label="Priority"
                    onChange={(e) => setObjectivePriorityFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Priorities</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Objectives Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Target Date</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {objectives.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography color="textSecondary">No objectives found.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    objectives.map((objective) => (
                      <TableRow key={objective.id}>
                        <TableCell>{objective.title}</TableCell>
                        <TableCell>{objective.category || '-'}</TableCell>
                        <TableCell>
                          {objective.employeeId ? (employees.find(e => e.id === objective.employeeId)?.name || 'Unknown') : '-'}
                        </TableCell>
                        <TableCell>
                          {objective.targetDate ? format(parseISO(objective.targetDate), 'MMM d, yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={objective.priority.charAt(0).toUpperCase() + objective.priority.slice(1)}
                            size="small"
                            color={getPriorityColor(objective.priority) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={objective.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            size="small"
                            color={getObjectiveStatusColor(objective.status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: '100%', maxWidth: 100 }}>
                              <LinearProgress variant="determinate" value={objective.progress} />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {objective.progress}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenViewObjective(objective)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditObjective(objective)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleOpenDeleteObjective(objective)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Training Create/Edit Dialog */}
      <Dialog open={trainingDialogOpen} onClose={handleCloseTrainingDialog} maxWidth="sm" fullWidth>
        <form onSubmit={trainingForm.handleSubmit(editingTraining ? handleUpdateTraining : handleCreateTraining)}>
          <DialogTitle>{editingTraining ? 'Edit Training' : 'Add Training'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                required
                {...trainingForm.register('title', { required: 'Title is required' })}
                error={!!trainingForm.formState.errors.title}
                helperText={trainingForm.formState.errors.title?.message}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                {...trainingForm.register('description')}
              />
              <TextField
                fullWidth
                label="Provider"
                {...trainingForm.register('provider')}
              />
              <Controller
                name="employeeId"
                control={trainingForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Employee (Optional)</InputLabel>
                    <Select {...field} label="Employee (Optional)" value={field.value || ''}>
                      <MenuItem value="">None</MenuItem>
                      {employees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                          {employee.name} ({employee.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Training Date"
                    type="date"
                    required
                    InputLabelProps={{ shrink: true }}
                    {...trainingForm.register('trainingDate', { required: 'Training date is required' })}
                    error={!!trainingForm.formState.errors.trainingDate}
                    helperText={trainingForm.formState.errors.trainingDate?.message}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Completion Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...trainingForm.register('completionDate')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Cost"
                    type="number"
                    inputProps={{ step: 0.01, min: 0 }}
                    {...trainingForm.register('cost', { valueAsNumber: true })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Controller
                    name="currency"
                    control={trainingForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Currency</InputLabel>
                        <Select {...field} label="Currency">
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <Controller
                name="status"
                control={trainingForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="planned">Planned</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <TextField
                fullWidth
                label="Certificate URL"
                {...trainingForm.register('certificateUrl')}
              />
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                {...trainingForm.register('notes')}
              />
              <Controller
                name="reminderDaysBefore"
                control={trainingForm.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Reminder Days Before"
                    helperText="Number of days before training date to send reminder"
                    inputProps={{ min: 0, max: 365 }}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 7)}
                  />
                )}
              />
              {!editingTraining && (
                <Controller
                  name="createExpense"
                  control={trainingForm.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value || false}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Create expense record for training cost"
                    />
                  )}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTrainingDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingTraining ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Objective Create/Edit Dialog */}
      <Dialog open={objectiveDialogOpen} onClose={handleCloseObjectiveDialog} maxWidth="sm" fullWidth>
        <form onSubmit={objectiveForm.handleSubmit(editingObjective ? handleUpdateObjective : handleCreateObjective)}>
          <DialogTitle>{editingObjective ? 'Edit Objective' : 'Add Objective'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                required
                {...objectiveForm.register('title', { required: 'Title is required' })}
                error={!!objectiveForm.formState.errors.title}
                helperText={objectiveForm.formState.errors.title?.message}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                {...objectiveForm.register('description')}
              />
              <TextField
                fullWidth
                label="Category"
                {...objectiveForm.register('category')}
              />
              <Controller
                name="employeeId"
                control={objectiveForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Employee (Optional)</InputLabel>
                    <Select {...field} label="Employee (Optional)" value={field.value || ''}>
                      <MenuItem value="">None</MenuItem>
                      {employees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                          {employee.name} ({employee.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Target Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...objectiveForm.register('targetDate')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Completion Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...objectiveForm.register('completionDate')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Controller
                    name="status"
                    control={objectiveForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="active">Active</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                          <MenuItem value="on_hold">On Hold</MenuItem>
                          <MenuItem value="cancelled">Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="priority"
                    control={objectiveForm.control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Priority</InputLabel>
                        <Select {...field} label="Priority">
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="medium">Medium</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
              <Controller
                name="progress"
                control={objectiveForm.control}
                rules={{ min: 0, max: 100 }}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    label="Progress (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                  />
                )}
              />
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                {...objectiveForm.register('notes')}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseObjectiveDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingObjective ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Training View Dialog */}
      <Dialog open={!!viewingTraining} onClose={handleCloseViewTraining} maxWidth="sm" fullWidth>
        <DialogTitle>Training Details</DialogTitle>
        <DialogContent>
          {viewingTraining && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Title</Typography>
                  <Typography variant="body1">{viewingTraining.title}</Typography>
                </Grid>
                {viewingTraining.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                    <Typography variant="body1">{viewingTraining.description}</Typography>
                  </Grid>
                )}
                {viewingTraining.provider && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Provider</Typography>
                    <Typography variant="body1">{viewingTraining.provider}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip
                    label={viewingTraining.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    size="small"
                    color={getTrainingStatusColor(viewingTraining.status) as any}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">Training Date</Typography>
                  <Typography variant="body1">
                    {format(parseISO(viewingTraining.trainingDate), 'MMM d, yyyy')}
                  </Typography>
                </Grid>
                {viewingTraining.completionDate && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Completion Date</Typography>
                    <Typography variant="body1">
                      {format(parseISO(viewingTraining.completionDate), 'MMM d, yyyy')}
                    </Typography>
                  </Grid>
                )}
                {viewingTraining.cost && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Cost</Typography>
                    <Typography variant="body1">
                      {formatCurrency(viewingTraining.cost, viewingTraining.currency)}
                    </Typography>
                  </Grid>
                )}
                {viewingTraining.certificateUrl && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Certificate</Typography>
                    <Link href={viewingTraining.certificateUrl} target="_blank" rel="noopener noreferrer">
                      View Certificate
                    </Link>
                  </Grid>
                )}
                {viewingTraining.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{viewingTraining.notes}</Typography>
                  </Grid>
                )}
                {viewingTraining.employeeId && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                    <Typography variant="body1">
                      {employees.find(e => e.id === viewingTraining.employeeId)?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                )}
                {viewingTraining.reminderDaysBefore && (
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Reminder Days Before</Typography>
                    <Typography variant="body1">{viewingTraining.reminderDaysBefore} days</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewTraining}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Objective View Dialog */}
      <Dialog open={!!viewingObjective} onClose={handleCloseViewObjective} maxWidth="sm" fullWidth>
        <DialogTitle>Objective Details</DialogTitle>
        <DialogContent>
          {viewingObjective && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Title</Typography>
                  <Typography variant="body1">{viewingObjective.title}</Typography>
                </Grid>
                {viewingObjective.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                    <Typography variant="body1">{viewingObjective.description}</Typography>
                  </Grid>
                )}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {viewingObjective.category && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Category</Typography>
                      <Typography variant="body1">{viewingObjective.category}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                    <Chip
                      label={viewingObjective.priority.charAt(0).toUpperCase() + viewingObjective.priority.slice(1)}
                      size="small"
                      color={getPriorityColor(viewingObjective.priority) as any}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                    <Chip
                      label={viewingObjective.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      size="small"
                      color={getObjectiveStatusColor(viewingObjective.status) as any}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">Progress</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Box sx={{ width: '100%', maxWidth: 200 }}>
                        <LinearProgress variant="determinate" value={viewingObjective.progress} />
                      </Box>
                      <Typography variant="body2">{viewingObjective.progress}%</Typography>
                    </Box>
                  </Grid>
                  {viewingObjective.targetDate && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Target Date</Typography>
                      <Typography variant="body1">
                        {format(parseISO(viewingObjective.targetDate), 'MMM d, yyyy')}
                      </Typography>
                    </Grid>
                  )}
                  {viewingObjective.completionDate && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Completion Date</Typography>
                      <Typography variant="body1">
                        {format(parseISO(viewingObjective.completionDate), 'MMM d, yyyy')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {viewingObjective.notes && (
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{viewingObjective.notes}</Typography>
                  </Grid>
                )}
                {viewingObjective.employeeId && (
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                    <Typography variant="body1">
                      {employees.find(e => e.id === viewingObjective.employeeId)?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              
              {/* Milestones Section */}
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Milestones</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenCreateMilestone(viewingObjective.id)}
                  >
                    Add Milestone
                  </Button>
                </Box>
                {viewingObjective.milestones && viewingObjective.milestones.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {viewingObjective.milestones.map((milestone) => (
                      <Accordion key={milestone.id}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                            <Typography>{milestone.title}</Typography>
                            <Chip
                              label={milestone.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              size="small"
                              color={milestone.status === 'completed' ? 'success' : milestone.status === 'in_progress' ? 'info' : 'default'}
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {milestone.description && (
                              <Typography variant="body2">{milestone.description}</Typography>
                            )}
                            {milestone.targetDate && (
                              <Typography variant="body2" color="textSecondary">
                                Target: {format(parseISO(milestone.targetDate), 'MMM d, yyyy')}
                              </Typography>
                            )}
                            {milestone.completionDate && (
                              <Typography variant="body2" color="textSecondary">
                                Completed: {format(parseISO(milestone.completionDate), 'MMM d, yyyy')}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => handleOpenEditMilestone(milestone, viewingObjective.id)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDeleteMilestone(viewingObjective.id, milestone.id)}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No milestones yet. Click "Add Milestone" to create one.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewObjective}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Create/Edit Dialog */}
      <Dialog open={milestoneDialogOpen} onClose={handleCloseMilestoneDialog} maxWidth="sm" fullWidth>
        <form onSubmit={milestoneForm.handleSubmit(editingMilestone ? handleUpdateMilestone : handleCreateMilestone)}>
          <DialogTitle>{editingMilestone ? 'Edit Milestone' : 'Add Milestone'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Title"
                required
                {...milestoneForm.register('title', { required: 'Title is required' })}
                error={!!milestoneForm.formState.errors.title}
                helperText={milestoneForm.formState.errors.title?.message}
              />
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                {...milestoneForm.register('description')}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Target Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...milestoneForm.register('targetDate')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Completion Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...milestoneForm.register('completionDate')}
                  />
                </Grid>
              </Grid>
              <Controller
                name="status"
                control={milestoneForm.control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
              <TextField
                fullWidth
                label="Order Index"
                type="number"
                inputProps={{ min: 0 }}
                {...milestoneForm.register('orderIndex', { valueAsNumber: true })}
                helperText="Lower numbers appear first"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMilestoneDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingMilestone ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Training Delete Confirmation Dialog */}
      <Dialog open={trainingDeleteDialogOpen} onClose={handleCloseDeleteTraining}>
        <DialogTitle>Delete Training Record</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this training record?
            <br />
            <br />
            Title: {trainingToDelete?.title}
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteTraining}>Cancel</Button>
          <Button onClick={handleDeleteTraining} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Objective Delete Confirmation Dialog */}
      <Dialog open={objectiveDeleteDialogOpen} onClose={handleCloseDeleteObjective}>
        <DialogTitle>Delete Objective</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this objective?
            <br />
            <br />
            Title: {objectiveToDelete?.title}
            <br />
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteObjective}>Cancel</Button>
          <Button onClick={handleDeleteObjective} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingAndObjectives;
